import React, { useState, useEffect, useRef } from "react";
import "./index.css";
import { Outlet, useNavigate } from "react-router-dom";
import { clearStorage, getAuthData, setStorageItem } from "../../Utils/storage";
import { notifyError } from "../../Utils/toastify";
import MenuSection from "../../Components/MenuSection";
import MenuIcon from "../../Assests/Icons/MenuIcon";
import {
  MdOutlineAccountBox,
  MdOutlineGroupAdd,
  MdOutlineCreate,
  MdPostAdd,
  MdOutlineRemoveRedEye,
  MdPersonOutline,
  MdOutlinePersonPin,
  MdOutlineTask,
  MdOutlineShield,
} from "react-icons/md";
import { useFetchUserRoles } from "../../API/Queries/useFetchUserRoles";
import { useIsFetching, useIsMutating } from "react-query";
import Loader from "Components/Loader";
import { ROUTES } from "Utils/routes";

const DashBoard = () => {
  const navigate = useNavigate();
  const authData = getAuthData();
  const [showMenu, setShowMenu] = useState(false);
  const { mutateAsync: fetchUserRoles } = useFetchUserRoles();

  const isFetching = useIsFetching();
  const isMutating = useIsMutating();

  const menuItems = [
    {
      id: "account",
      label: "Account Management",
      privilegeName: "ACCOUNT_MANAGEMENT_ADMIN_TOOL",
      icon: MdOutlineAccountBox,
      submenu: [
        {
          id: "sales-person",
          label: "Sales Person",
          icon: MdOutlineGroupAdd,
          route: ROUTES.DASHBOARD,
        },
        {
          id: "customer",
          label: "Customer",
          icon: MdOutlineGroupAdd,
          route: ROUTES.DASHBOARD_CUSTOMERS,
        },
        {
          id: "market-update",
          label: "Market Update",
          icon: MdOutlineTask,
          route: ROUTES.MARKET_UPDATE,
        },
      ],
    },
    {
      id: "provider-tool",
      label: "Provider Tool",
      privilegeName: "PROVIDER_TOOL_ADMIN_TOOL",
      icon: MdOutlineCreate,
      submenu: [
        {
          id: "create-provider",
          label: "Create Provider",
          icon: MdPostAdd,
          route: ROUTES.PROVIDER_CREATE,
        },
        {
          id: "view-providers",
          label: "View Providers",
          icon: MdOutlineRemoveRedEye,
          route: ROUTES.PROVIDER_VIEW,
        },
        {
          id: "create-tool",
          label: "Create Tool",
          icon: MdPostAdd,
          route: ROUTES.TOOL_CREATE,
        },
      ],
    },
    {
      id: "user-data",
      label: "User Data Correction",
      privilegeName: "USER_DATA_CORRECTION_ADMIN_TOOL",
      icon: MdPersonOutline,
      submenu: [
        {
          id: "user-profile",
          label: "User Profile",
          icon: MdOutlinePersonPin,
          route: ROUTES.USER_PROFILE,
        },
        {
          id: "kyc-details",
          label: "KYC Details",
          icon: MdOutlineTask,
          route: ROUTES.KYC_DETAILS,
        },
        {
          id: "market-update",
          label: "Market Update",
          icon: MdOutlineTask,
          route: ROUTES.MARKET_UPDATE,
        },
      ],
    },
    {
      id: "dam",
      label: "DAM",
      privilegeName: "DAM_ADMIN_TOOL",
      icon: MdOutlineAccountBox,
      submenu: [
        {
          id: "collection-request-template",
          label: "Collection Request Template",
          icon: MdPostAdd,
          route: "/dam/collection-request-template",
        },
        {
          id: "digital-asset-definition",
          label: "Digital Asset Definition",
          icon: MdPostAdd,
          route: "/dam/digital-asset-definition",
        },
      ],
    },
    {
      id: "feature-flip",
      label: "Feature Flip",
      privilegeName: "FEATURE_TOGGLE_ADMIN_TOOL",
      icon: MdOutlineTask,
      route: ROUTES.FEATURE_FLIP,
    },
    {
      id: "internal",
      label: "Consent portal",
      icon: MdOutlineShield,
      route: "/internal/purposes",
      newTab: true,
    },
  ];

  const fetchUserRolesRef = useRef(fetchUserRoles);
  fetchUserRolesRef.current = fetchUserRoles;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const authDataRef = useRef(authData);
  authDataRef.current = authData;

  useEffect(() => {
    const fetchUserRoles = fetchUserRolesRef.current;
    const navigate = navigateRef.current;
    const authData = authDataRef.current;
    const fetchRole = async () => {
      const res = await fetchUserRoles(authData?.ownerUserId);
      if (res) {
        const { roles, privileges } = res?.data || {};

        const innerPrivilegeMap = new Map();

        //flat privileges from all roles and push to innerPrivilegesMap
        roles.forEach((role) => {
          role.privileges.forEach((priv) => {
            innerPrivilegeMap.set(priv.name, priv);
          });
        });

        //if outer privileges has grantType = ALLOWED then add to innerPrivilegesMap, else remove that from innerPrivilegesMap -> if it has same privilege name
        privileges.forEach(({ privilege, grantType }) => {
          if (grantType === "ALLOWED") {
            innerPrivilegeMap.set(privilege.name, privilege);
          } else if (innerPrivilegeMap.has(privilege.name)) {
            innerPrivilegeMap.delete(privilege.name);
          }
        });

        const privilegeNames = Array.from(innerPrivilegeMap.values()).map(
          (p) => p.name
        );

        setStorageItem("privileges", privilegeNames);
        if (privilegeNames.length === 0) {
          notifyError("You do not have access!");
          clearStorage();
          navigate(ROUTES.LOGIN_EMAIL);
        }
      }
    };

    if (!authData?.expires_in && authData?.userId) {
      fetchRole();
    }
  }, [authData?.userId, authData?.expires_in]);

  const toggleMenuSection = () => {
    setShowMenu((prev) => !prev);
  };

  const closeMenuSection = () => {
    setShowMenu(false);
  };
  const isLoading = isFetching !== 0 || isMutating !== 0;

  return (
    <div className="dashboard-parent-container">
      {isLoading && <Loader />}
      <div className="top-bar">
        <div className="menu-styles" onClick={toggleMenuSection}>
          <MenuIcon />
        </div>
        <b>Platform Admin Tool</b>
      </div>

      {showMenu && (
        <MenuSection
          isOpen={showMenu}
          onClose={closeMenuSection}
          menuItems={menuItems}
        />
      )}
      <Outlet />
    </div>
  );
};

export default DashBoard;
